/* eslint-disable no-unused-vars */
import { type PlaidLinkType } from '@egw/api'
import { type ReactNode } from 'react'
import { type PlaidLinkOnSuccessMetadata } from 'react-plaid-link'

export * from 'react-plaid-link'

/**
 * State enum for component rendering stages
 */
export enum PlaidMultiLinkState {
  // Account link type selection
  ACCOUNT_LINK_TYPE_SELECTION = 'account_link_type_selection', // Select Account Linking Type (Banking | Investments)

  INSTITUTION_MANAGEMENT_SELECTION = 'institution_management_selection', // Institution management selection (Select Relink | Update | Remove Institution)

  INITIAL_INSTITUTION_LINKING_IN_PROGRESS = 'initial_institution_linking_in_progress', // Initial institution linking in progress

  RELINKING_INSTITUTION_IN_PROGRESS = 'relinking_institution_in_progress', // Relink institution in progress
  UPDATING_INSTITUTION_IN_PROGRESS = 'updating_institution_in_progress', // Update institution in progress

  SUCCESS = 'success', // Success for Account Link | Relink | Remove Link
  ERROR = 'error', // Error for Account Link | Relink | Remove Link
}

/**
 * Error types for Plaid Multi Link process
 */
export enum PlaidErrorType {
  // Initial institution link errors
  INITIAL_INSTITUTION_LINK_TOKEN_CREATION = 'initial_institution_link_token_creation',
  INITIAL_INSTITUTION_LINK_ERROR = 'initial_institution_link_error',

  // Institution removal errors
  INSTITUTION_REMOVAL_ERROR = 'institution_removal_error',

  // Relink institution errors
  RELINK_INSTITUTION_TOKEN_CREATION = 'relink_institution_token_creation',
  RELINK_INSTITUTION_ERROR = 'relink_institution_error',

  // Updating institution errors
  UPDATING_INSTITUTION_TOKEN_CREATION = 'updating_institution_token_creation',
  UPDATING_INSTITUTION_ERROR = 'updating_institution_error',
}

/**
 * Success types for Plaid Multi Link process
 */
export enum PlaidSuccessType {
  INITIAL_INSTITUTION_LINK = 'initial_institution_link', // Link an institution
  RELINK_INSTITUTION = 'relink_institution', // Relink an institution
  REMOVE_INSTITUTION = 'remove_institution', // Remove an institution
  UPDATING_INSTITUTION = 'updating_institution', // Manage linked accounts for an institution
}

/**
 * Update action types for account management
 */
export enum UpdateActionType {
  UPDATING_INSTITUTION = 'updating_institution', // Manage linked accounts for an institution
  RELINK_INSTITUTION = 'relink_institution', // Relink an institution
  REMOVE_INSTITUTION = 'remove_institution', // Remove an institution
}

/**
 * Hook return interface for usePlaidMultiLink
 */
export interface PlaidMultiLinkHookReturn {
  // Modal state
  isOpen: boolean
  alias?: number
  modalTitle?: string
  institutionId?: string
  isChaseBank: boolean

  // Plaid flow state
  currentState: PlaidMultiLinkState
  selectedLinkType: PlaidLinkType | undefined
  plaidLinkToken: string | undefined
  INITIAL_STATE: PlaidMultiLinkState

  // Error and success state
  currentErrorType?: PlaidErrorType
  currentSuccessType?: PlaidSuccessType

  // Loading states
  isCreatingPlaidMultiLinkToken: boolean
  isUnlinkingPlaidExternalInstitution: boolean
  isInitiatingRelinkPlaidExternalInstitution: boolean
  isCompletingRelinkPlaidExternalAccount: boolean
  isUpdatingPlaidExternalInstitution: boolean
  isCompletingUpdatePlaidExternalAccount: boolean
  // Show Relink Button if account is expired or expiring soon
  showRelink: boolean
  // Modal control methods
  openModal: (options?: { institutionId?: string; modalTitle?: string }) => void
  closeModal: () => void

  // Plaid flow handlers
  handleAccountTypeSelection: (linkType: PlaidLinkType) => void //Link Account type selection for Banking | Investments
  handleUpdateActionSelection: (actionType: UpdateActionType) => void // Action selection for relink or remove flows
  handleTryAgain: () => void // Try again either recreates new token for new or relinking flows OR attempts to remove the account
  handlePlaidSuccess: (_metadata: PlaidLinkOnSuccessMetadata) => void // Success handler for both link and relink flows
  handlePlaidError: () => void // Error handler for both link and relink flows
  handlePlaidExit: () => void // Exit handler for both link and relink flows
}

/**
 * Props interface for AccountSelection component
 */
export interface AccountSelectionProps {
  /** Callback function when user selects account type */
  onAccountTypeSelection: (linkType: PlaidLinkType) => void
  /** Loading state for token creation */
  isCreatingPlaidMultiLinkToken: boolean
  /** Currently selected link type */
  selectedLinkType?: PlaidLinkType
}

/**
 * Props interface for AccountUpdateSelection component
 */
export interface InstitutionUpdateSelectionProps {
  /** Callback function when user selects update action */
  onUpdateActionSelection: (actionType: UpdateActionType) => void
  showRelink: boolean
  isChaseBank: boolean
}

/**
 * Props interface for PlaidSDKWrapper component
 */
export interface PlaidSDKWrapperProps {
  /** Plaid link token */
  token: string
  /** Success callback */
  onSuccess: (_metadata: PlaidLinkOnSuccessMetadata) => void
  /** Error callback */
  onError: () => void
  /** Exit callback */
  onExit: () => void
  /** Child components */
  children: ReactNode
}
